import { HttpClient, HttpParams } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { BehaviorSubject } from 'rxjs';
import { environment } from 'environments/environment';
import { JwtAuthService } from './auth/jwt-auth.service';
import { toCamelCase } from 'js-convert-case';
import { RouteProtectionService } from './route-protection.service';
import { ProfileType, UserRole } from '../enums/profile-type.enum';
import { User } from '../models/user.model';

export type MenuType =
  | 'admin-menu'
  | 'super-admin-menu'
  | 'portal-user-menu'
  | 'business-user-menu'
  | 'driver-menu'
  | 'telehealth-pharmacy-menu'
  | 'doctor-user-menu'
  | 'nurse-practitioner-menu'
  | 'nurse-menu'
  | 'radiology-user-menu'
  | 'clinical-operations-menu';

export interface OrderCount {
  online: online_counts;
  in_store: in_store_counts;
  all_orders: all_orders_counts;
  closed: closed_counts;
  telehealth: telehealth_counts;
}
export type OrderCountTypes = OrderCount[keyof OrderCount];

export interface online_counts {
  pending_approval: number;
  clinic_to_review: number;
  clinic_to_action: number;
  price_request: number;
  processing: number;
  delivery_pickup: number;
}

export interface in_store_counts {
  pending_approval: number;
  clinic_to_review: number;
  clinic_to_action: number;
  price_request: number;
  processing: number;
  delivery_pickup: number;
}

export interface all_orders_counts {
  clinic_to_review: number;
  care_call: number;
  clinic_to_action: number;
  request_issued: number;
  pending_approval: number;
  price_request: number;
  processing: number;
  delivery_pickup: number;
  active: number;
  exception: number;
}

export interface closed_counts {
  completed: number;
  cancelled: number;
}

export interface telehealth_counts {
  pending: number;
  script_issued: number;
  awaiting_delivery?: number;
  completed: number;
  cancelled: number;
}

export interface IMenuItem {
  type: string; // Possible values: link/dropDown/icon/separator/extLink
  key: string; //Unique key to determine value
  name?: string; // Used as display text for item and title for separator type
  state?: string; // Router state
  icon?: string; // Material icon name
  svgIcon?: string; // UI Lib icon name
  tooltip?: string; // Tooltip text
  disabled?: boolean; // If true, item will not be appeared in sidenav.
  sub?: IChildItem[]; // Dropdown items
  badges?: IBadge[];
  value?: string;
  color1?: string;
}
interface IChildItem {
  type?: string;
  name: string; // Display text
  state?: string; // Router state
  icon?: string; // Material icon name
  svgIcon?: string; // UI Lib icon name
  sub?: IChildItem[];
  value?: string;
  color1?: string;
  id?: string;
}

interface IBadge {
  color: string; // primary/accent/warn/hex color codes(#fff000)
  value: string; // Display text
}

@Injectable({ providedIn: 'root' })
export class NavigationService {
  adminMenu: IMenuItem[] = [
    {
      name: 'Create New Order',
      key: 'create-order',
      type: 'link',
      icon: 'description',
      state: 'orders/create-order',
    },
    {
      name: 'Orders',
      key: 'online-orders',
      type: 'dropDown',
      tooltip: 'Dashboard',
      svgIcon: 'ulb_orders',
      state: 'orders/online',
      value: '',
      color1: 'warn',
      sub: [
        // { name: 'Clinic to Review', state: 'clinic_to_review', value: '', color1: "#72cb38" },
        { name: 'Waiting Room', state: 'clinic_to_action', value: '', color1: '#72cb38' },
        { name: 'Pending Approval', state: 'pending_approval', value: '', color1: '#ff7901' },
        { name: 'Price Request', state: 'price_request', value: '', color1: '#1e98d5' },
        { name: 'Ready to Process', state: 'processing', value: '', color1: '#1e98d5' },
        { name: 'Delivery/Pickup', state: 'delivery_pickup', value: '', color1: '#1e98d5' },
        { name: 'Pending cancellation', state: 'pending_cancellation', value: '', color1: '#1e98d5' },
      ],
    },
    {
      name: 'Telehealth',
      key: 'telehealth',
      type: 'dropDown',
      tooltip: 'Dashboard',
      //TODO: change icon
      svgIcon: 'ulb_telehealth',
      state: 'telehealth',
      value: '',
      sub: [
        {
          name: 'Telehealth Request',
          type: 'link',
          state: 'telehealth-request',
        },
        {
          name: 'Consultation Request',
          type: 'link',
          state: 'new-telehealth-request'
        },
        {
          name: 'Pending',
          type: 'link',
          state: 'pending',
          value: '',
          color1: '#ff7901',
        },
        {
          name: 'Script Issued',
          type: 'link',
          state: 'script-issued',
          value: '',
          color1: '#1e98d5',
        },
      ],
    },
    // {
    //   name: 'In Store',
    //   key: 'in-store-orders',
    //   type: 'dropDown',
    //   tooltip: 'Dashboard',
    //   svgIcon: 'ulb_server',
    //   state: 'orders/in_store',
    //   value: '',
    //   color1: "warn",
    //   sub: [
    //     // { name: 'Clinic to Review', state: 'clinic_to_review', value: '', color1: "#72cb38" },
    //     { name: 'Pending Approval', state: 'pending_approval', value: '', color1: "#ff7901" },
    //     { name: 'Clinic to Action', state: 'clinic_to_action', value: '', color1: "#72cb38" },
    //     { name: 'Ready to Process', state: 'processing', value: '', color1: "#1e98d5" },
    //     { name: 'Delivery/Pickup', state: 'delivery_pickup', value: '', color1: "#1e98d5" },
    //   ]
    // },
    {
      name: 'All Orders',
      key: 'all-orders',
      type: 'dropDown',
      tooltip: 'Dashboard',
      svgIcon: 'ulb_orders',
      state: 'orders/admin',
      value: '',
      color1: 'warn',
      sub: [
        { name: 'Clinic to Review', state: 'clinic_to_review', value: '', color1: '#72cb38' },
        { name: 'Clinic to Update', state: 'clinic_to_update', value: '', color1: '#1e98d5' },
        { name: 'Appointments', state: 'waiting_room', value: '', color1: '#1e98d5' },
        { name: 'Auto Allocation', state: 'auto_allocation', value: '', color1: '#1e98d5' },
        { name: 'Waiting Room', state: 'clinic_to_action', value: '', color1: '#72cb38' },
        { name: 'Urgent', state: 'escalated_orders', value: '', color1: '#F0A04B' },
        {
          name: 'Waiting for Info',
          state: 'waiting_for_information',
          value: '',
          color1: '#72cb38',
        },
        { name: 'Request Issued', state: 'request_issued', value: '', color1: '#1e98d5' },
        { name: 'Pending Approval', state: 'pending_approval', value: '', color1: '#ff7901' },
        { name: 'Price Request', state: 'price_request', value: '', color1: '#1e98d5' },
        { name: 'Ready to Process', state: 'processing', value: '', color1: '#1e98d5' },
        { name: 'Delivery/Pickup', state: 'delivery_pickup', value: '', color1: '#1e98d5' },
        { name: 'Active', state: 'active', value: '', color1: '#ff3d57' },
        { name: 'Exception', state: 'exception', value: '', color1: 'pink' },
        { name: 'Pending Batch Processing', state: 'pending_batch_processing', value: '', color1: '#ff7901' },
        { name: 'Paper Scripts', state: 'paper_scripts', value: '', color1: '#ff3d57' },
        { name: 'Care Call', state: 'care_call', value: '', color1: 'pink' }
      ],
    },
    {
      name: 'Closed',
      key: 'closed-orders',
      type: 'dropDown',
      tooltip: 'Dashboard',
      svgIcon: 'ulb_closed',
      state: 'orders/closed',
      value: '',
      color1: 'warn',
      sub: [
        { name: 'Completed', state: 'completed', value: '', color1: 'grey' },
        { name: 'Cancelled', state: 'cancelled', value: '', color1: 'grey' },
      ],
    },
    {
      name: 'Programs',
      key: 'patient-programs',
      type: 'link',
      tooltip: 'Programs',
      svgIcon: 'ulb_checklist',
      state: 'patient-programs',
    },
    {
      name: 'Users',
      key: 'users',
      type: 'dropDown',
      tooltip: 'Dashboard',
      svgIcon: 'ulb_users',
      state: 'users',
      value: '',
      sub: [
        {
          name: 'Invite',
          type: 'link',
          icon: 'person_add',
          state: 'invite',
        },
        {
          name: 'Create User',
          type: 'link',
          icon: 'person_add',
          state: 'create-user',
        },
        {
          name: 'Manage User',
          type: 'link',
          icon: 'person_add',
          state: 'manage-user',
        },
      ],
    },
    {
      name: 'Resources',
      key: 'resources',
      type: 'dropDown',
      tooltip: 'Dashboard',
      //TODO: change icon
      svgIcon: 'ulb_resources',
      state: 'resources',
      value: '',
      sub: [
        {
          name: 'Create New Resource',
          type: 'link',
          icon: 'add_circle',
          state: 'create-resource',
        },
        {
          name: 'On Boarding',
          type: 'link',
          icon: 'ulb_server',
          state: 'on-boarding',
        },
        {
          name: 'Setup',
          type: 'link',
          icon: 'ulb_server',
          state: 'setup',
        },
        {
          name: 'Training',
          type: 'link',
          icon: 'ulb_server',
          state: 'training-guide',
        },
        {
          name: 'Program',
          type: 'link',
          icon: 'person_add',
          state: 'pharmacy-program'
        },
        {
          name: 'Treatment guides',
          type: 'link',
          icon: 'ulb_server',
          state: 'treatment-guides',
        },
        {
          name: 'Learning and Development',
          type: 'link',
          icon: 'ulb_server',
          state: 'learning-and-development',
        },
        {
          name: 'Telehealth User Guide',
          type: 'link',
          icon: 'ulb_server',
          state: 'telehealth-user-guide'
        },
        {
          name: 'Feature Tours',
          type: 'link',
          icon: 'tour',
          state: 'feature-tours',
        },
      ]
    },
    {
      name: 'Reports',
      key: 'reports',
      type: 'dropDown',
      icon: 'description',
      state: 'reports',
      sub: [
        { name: 'Dashboard', state: 'dashboard' },
        { name: 'Location', state: 'locations' },
        { name: 'Partners', state: 'partners' },
        { name: 'Telehealth', state: 'telehealth' },
        { name: 'Programs', state: 'programs' },
      ],
    },
    {
      name: 'Stores',
      key: 'stores',
      type: 'dropDown',
      tooltip: 'Dashboard',
      svgIcon: 'ulb_stores',
      state: 'stores',
      sub: [
        { name: 'New Store', state: 'create-store' },
        { name: 'Store Details', state: 'store-detail' },
        { name: 'Store Listing', state: 'listing' },
        // { name: 'Delivery Options', state: 'analytics' },
        // { name: 'Delivery Partners', state: 'deliverypartners' },
      ],
    },
    {
      name: 'Clinician',
      key: 'clinician',
      type: 'dropDown',
      tooltip: 'Dashboard',
      svgIcon: 'ulb_server',
      state: 'clinician',
      sub: [
        { name: 'Clinician', state: 'profiles' },
        { name: 'Shifts', state: 'shifts' },
        { name: 'Day Sheet', state: 'day-sheet' },
      ],
    },
    {
      name: 'Products',
      key: 'products',
      type: 'dropDown',
      tooltip: 'Dashboard',
      svgIcon: 'ulb_products',
      state: 'products',
      sub: [
        { name: 'Master Catalogue', state: 'master-catalogue' },
        { name: 'Catalogue', state: 'catalogue' },
        { name: 'Promotions', state: 'promotions' },
        { name: 'Categories', state: 'categories' },
        { name: 'Brands', state: 'brands' },
      ],
    },
    {
      name: 'Rendr',
      key: 'rendr',
      type: 'link',
      tooltip: 'Dashboard',
      svgIcon: 'ulb_rendr',
      state: 'orders/rendr',
    },
    {
      name: 'Billing',
      key: 'billing',
      type: 'dropDown',
      tooltip: 'Billing',
      svgIcon: 'ulb_billing',
      state: 'billing',
      // Legacy Stripe Subscriptions and Patient Programs coexist, so the
      // Subscriptions screen stays available for patients still on the
      // legacy billing model.
      sub: [
        { name: 'Subscriptions', state: 'subscriptions' },
      ],
    },
    {
      name: 'Admin',
      key: 'admin',
      type: 'dropDown',
      tooltip: 'Dashboard',
      svgIcon: 'ulb_admin',
      state: 'admin',
      sub: [
        { name: 'Known As', state: 'default' },
        { name: 'Notification Monitor', state: 'monitor' },
        { name: 'Restricted Meds', state: 'restricted-meds' },
        { name: 'Manage Banner', state: 'manage-banner' },
        { name: 'Telehealth Config', state: 'telehealth-configurations' },
        { name: 'Approval Requests', state: 'approval-requests' },
        { name: 'Facility Management', state: 'facility-management' },
        { name: 'Performer Maint...', state: 'performer-maintenance' },
      ],
    },
    {
      name: 'Locumate',
      key: 'locumate',
      type: 'link',
      tooltip: 'Dashboard',
      svgIcon: 'locumate_logo',
      state: 'locumate',
    },
    {
      name: 'Fluger',
      key: 'fluger',
      type: 'link',
      tooltip: 'Dashboard',
      svgIcon: 'fluger_logo',
      state: 'fluger',
    },
  ];

  portalUserMenu: IMenuItem[] = [
    {
      name: 'Create New Order',
      type: 'link',
      key: 'create-order',
      icon: 'description',
      state: 'orders/create-order',
    },
    {
      name: 'Orders',
      key: 'online-orders',
      type: 'dropDown',
      tooltip: 'Dashboard',
      svgIcon: 'ulb_orders',
      state: 'orders/online',
      value: '',
      color1: 'warn',
      sub: [
        // { name: 'Clinic to Review', state: 'clinic_to_review', value: '', color1: "#72cb38" },
        { name: 'Pending Approval', state: 'pending_approval', value: '', color1: '#ff7901' },
        { name: 'Price Request', state: 'price_request', value: '', color1: '#1e98d5' },
        { name: 'Ready to Process', state: 'processing', value: '', color1: '#1e98d5' },
        { name: 'Delivery/Pickup', state: 'delivery_pickup', value: '', color1: '#1e98d5' },
        { name: 'Pending cancellation', state: 'pending_cancellation', value: '', color1: '#1e98d5' }
      ],
    },
    {
      name: 'Telehealth',
      key: 'telehealth',
      type: 'dropDown',
      tooltip: 'Dashboard',
      //TODO: change icon
      svgIcon: 'ulb_telehealth',
      state: 'telehealth',
      value: '',
      sub: [
        {
          name: 'Telehealth Request',
          type: 'link',
          state: 'telehealth-request',
        },
        {
          name: 'Pending',
          type: 'link',
          state: 'pending',
          value: '',
          color1: '#ff7901',
        },
        {
          name: 'Script Issued',
          type: 'link',
          state: 'script-issued',
          value: '',
          color1: '#1e98d5',
        },
      ],
    },
    // {
    //   name: 'In Store',
    //   key: 'in-store-orders',
    //   type: 'dropDown',
    //   tooltip: 'Dashboard',
    //   svgIcon: 'ulb_server',
    //   state: 'orders/in_store',
    //   value: '',
    //   color1: "warn",
    //   sub: [
    //     { name: 'Pending Approval', state: 'pending_approval', value: '', color1: "#ff7901" },
    //     { name: 'Clinic to Action', state: 'clinic_to_action', value: '', color1: "#72cb38" },
    //     { name: 'Ready to Process', state: 'processing', value: '', color1: "#1e98d5" },
    //     { name: 'Delivery/Pickup', state: 'delivery_pickup', value: '', color1: "#1e98d5" },
    //   ]
    // },
    {
      name: 'Closed',
      key: 'closed-orders',
      type: 'dropDown',
      tooltip: 'Dashboard',
      svgIcon: 'ulb_closed',
      state: 'orders/closed',
      value: '',
      color1: 'warn',
      sub: [
        { name: 'Completed', state: 'completed', value: '', color1: 'grey' },
        { name: 'Cancelled', state: 'cancelled', value: '', color1: 'grey' },
      ],
    },
    {
      name: 'Resources',
      key: 'resources',
      type: 'dropDown',
      tooltip: 'Dashboard',
      //TODO: change icon
      svgIcon: 'ulb_resources',
      state: 'resources',
      value: '',
      sub: [
        {
          name: 'On Boarding',
          type: 'link',
          icon: 'ulb_server',
          state: 'on-boarding',
        },
        {
          name: 'Setup',
          type: 'link',
          icon: 'ulb_server',
          state: 'setup',
        },
        {
          name: 'Training',
          type: 'link',
          icon: 'ulb_server',
          state: 'training-guide',
        },
        {
          name: 'Program',
          type: 'link',
          icon: 'person_add',
          state: 'pharmacy-program'
        },
        {
          name: 'Telehealth User Guide',
          type: 'link',
          icon: 'ulb_server',
          state: 'telehealth-user-guide',
        },
        {
          name: 'Collateral',
          type: 'link',
          icon: 'ulb_server',
          state: 'collateral'
        },
      ],
    },
    // {
    //   name: "Users",
    //   key: 'users',
    //   type: 'dropDown',
    //   tooltip: 'Dashboard',
    //   //TODO: change icon
    //   svgIcon: 'ulb_server',
    //   state: 'users',
    //   value: '',
    //   sub: [
    //   ]
    // },

    {
      name: 'Reports',
      key: 'reports',
      type: 'dropDown',
      icon: 'description',
      state: 'reports',
      sub: [{ name: 'Location', state: 'locations' }],
    },
    {
      name: 'Stores',
      key: 'stores',
      type: 'dropDown',
      tooltip: 'Dashboard',
      svgIcon: 'ulb_stores',
      state: 'stores',
      sub: [{ name: 'Store Details', state: 'store-detail' }],
    },
    {
      name: 'Products',
      key: 'products',
      type: 'dropDown',
      tooltip: 'Dashboard',
      svgIcon: 'ulb_products',
      state: 'products',
      sub: [
        { name: 'Catalogue', state: 'catalogue' },
        { name: 'Categories', state: 'categories' },
        { name: 'Brands', state: 'brands' },
      ],
    },
    {
      name: 'Locumate',
      key: 'locumate',
      type: 'link',
      tooltip: 'Dashboard',
      svgIcon: 'locumate_logo',
      state: 'locumate',
    },
    {
      name: 'Fluger',
      key: 'fluger',
      type: 'link',
      tooltip: 'Dashboard',
      svgIcon: 'fluger_logo',
      state: 'fluger',
    },
  ];
  doctorUserMenu: IMenuItem[] = [
    {
      name: 'Waiting Room',
      key: 'Waiting Room',
      type: 'link',
      tooltip: 'Dashboard',
      svgIcon: 'ulb_orders',
      state: 'orders/online/clinic_to_action',
      value: '',
      color1: 'warn',

    },
    {
      name: 'Report',
      key: 'Report',
      type: 'link',
      tooltip: 'Dashboard',
      icon: 'description',
      state: 'orders/closed/completed',
      value: '',
      color1: 'warn',
    },
    {
      name: 'Day Sheet',
      key: 'day-sheet',
      type: 'link',
      tooltip: 'Day Sheet',
      icon: 'today',
      state: 'clinician/day-sheet',
      value: '',
    },
    {
      name: 'Resources',
      key: 'resources',
      type: 'dropDown',
      tooltip: 'Dashboard',
      //TODO: change icon
      svgIcon: 'ulb_resources',
      state: 'resources',
      value: '',
      sub: [
        {
          name: 'On Boarding',
          type: 'link',
          icon: 'ulb_server',
          state: 'on-boarding',
        },
        {
          name: 'Setup',
          type: 'link',
          icon: 'ulb_server',
          state: 'setup',
        },
        {
          name: 'Training',
          type: 'link',
          icon: 'ulb_server',
          state: 'training-guide',
        },
        {
          name: 'Treatment guides',
          type: 'link',
          icon: 'ulb_server',
          state: 'treatment-guides',
        },
        {
          name: 'Learning and Development',
          type: 'link',
          icon: 'ulb_server',
          state: 'learning-and-development',
        }
      ]
    },
  ];

  nursePractitionerMenu: IMenuItem[] = this.doctorUserMenu;
  nurseMenu: IMenuItem[] = this.doctorUserMenu;

  businessUserMenu: IMenuItem[] = [
    // {
    //   name: 'Create New Order',
    //   type: 'link',
    //   key: 'create-order',
    //   icon: 'description',
    //   state: 'orders/create-order',
    // },
    {
      name: 'Orders',
      key: 'online-orders',
      type: 'dropDown',
      tooltip: 'Dashboard',
      svgIcon: 'ulb_orders',
      state: 'orders/online',
      value: '',
      color1: 'warn',
      sub: [
        // { name: 'Clinic to Review', state: 'clinic_to_review', value: '', color1: "#72cb38" },
        { name: 'Pending Approval', state: 'pending_approval', value: '', color1: '#ff7901' },
        { name: 'Price Request', state: 'price_request', value: '', color1: '#1e98d5' },
        { name: 'Ready to Process', state: 'processing', value: '', color1: '#1e98d5' },
        { name: 'Delivery/Pickup', state: 'delivery_pickup', value: '', color1: '#1e98d5' },
        { name: 'Pending cancellation', state: 'pending_cancellation', value: '', color1: '#1e98d5' }
      ],
    },
    {
      name: 'Telehealth',
      key: 'telehealth',
      type: 'dropDown',
      tooltip: 'Dashboard',
      //TODO: change icon
      svgIcon: 'ulb_telehealth',
      state: 'telehealth',
      value: '',
      sub: [
        {
          name: 'Telehealth Request',
          type: 'link',
          state: 'telehealth-request',
        },
        {
          name: 'Pending',
          type: 'link',
          state: 'pending',
          value: '',
          color1: '#ff7901',
        },
        {
          name: 'Script Issued',
          type: 'link',
          state: 'script-issued',
          value: '',
          color1: '#1e98d5',
        },
      ],
    },
    // {
    //   name: 'In Store',
    //   key: 'in-store-orders',
    //   type: 'dropDown',
    //   tooltip: 'Dashboard',
    //   svgIcon: 'ulb_server',
    //   state: 'orders/in_store',
    //   value: '',
    //   color1: "warn",
    //   sub: [
    //     { name: 'Pending Approval', state: 'pending_approval', value: '', color1: "#ff7901" },
    //     { name: 'Clinic to Action', state: 'clinic_to_action', value: '', color1: "#72cb38" },
    //     { name: 'Ready to Process', state: 'processing', value: '', color1: "#1e98d5" },
    //     { name: 'Delivery/Pickup', state: 'delivery_pickup', value: '', color1: "#1e98d5" },
    //   ]
    // },
    {
      name: 'Closed',
      key: 'closed-orders',
      type: 'dropDown',
      tooltip: 'Dashboard',
      svgIcon: 'ulb_closed',
      state: 'orders/closed',
      value: '',
      color1: 'warn',
      sub: [
        { name: 'Completed', state: 'completed', value: '', color1: 'grey' },
        { name: 'Cancelled', state: 'cancelled', value: '', color1: 'grey' },
      ],
    },
    {
      name: 'Users',
      key: 'users',
      type: 'dropDown',
      tooltip: 'Users',
      svgIcon: 'ulb_users',
      state: 'users',
      value: '',
      sub: [
        {
          name: 'Create User',
          type: 'link',
          icon: 'person_add',
          state: 'create-user',
        },
        {
          name: 'Manage User',
          type: 'link',
          icon: 'person_add',
          state: 'manage-user',
        },
      ],
    },
    {
      name: 'Resources',
      key: 'resources',
      type: 'dropDown',
      tooltip: 'Dashboard',
      //TODO: change icon
      svgIcon: 'ulb_resources',
      state: 'resources',
      value: '',
      sub: [
        {
          name: 'On Boarding',
          type: 'link',
          icon: 'ulb_server',
          state: 'on-boarding',
        },
        {
          name: 'Setup',
          type: 'link',
          icon: 'ulb_server',
          state: 'setup',
        },
        {
          name: 'Training',
          type: 'link',
          icon: 'ulb_server',
          state: 'training-guide',
        },
        {
          name: 'Program',
          type: 'link',
          icon: 'person_add',
          state: 'pharmacy-program'
        },
        {
          name: 'Telehealth User Guide',
          type: 'link',
          icon: 'ulb_server',
          state: 'telehealth-user-guide',
        },
      ],
    },
    {
      name: 'Reports',
      key: 'reports',
      type: 'dropDown',
      icon: 'description',
      state: 'reports',
      sub: [
        { name: 'Location', state: 'locations' },
        { name: 'Programs', state: 'programs' },
      ],
    },
    {
      name: 'Stores',
      key: 'stores',
      type: 'dropDown',
      tooltip: 'Dashboard',
      svgIcon: 'ulb_stores',
      state: 'stores',
      sub: [
        { name: 'New Store', state: 'create-store' },
        { name: 'Store Details', state: 'store-detail' },
        { name: 'Store Listing', state: 'listing' },
      ],
    },
    {
      name: 'Products',
      key: 'products',
      type: 'dropDown',
      tooltip: 'Dashboard',
      svgIcon: 'ulb_products',
      state: 'products',
      sub: [
        { name: 'Master Catalogue', state: 'master-catalogue' },
        { name: 'Catalogue', state: 'catalogue' },
        { name: 'Promotions', state: 'promotions' },
        { name: 'Categories', state: 'categories' },
        { name: 'Brands', state: 'brands' },
      ],
    },
  ];

  driverMenu: IMenuItem[] = [
    {
      name: 'Orders',
      key: 'online-orders',
      type: 'dropDown',
      tooltip: 'Dashboard',
      svgIcon: 'ulb_orders',
      state: 'orders',
      value: '',
      color1: 'warn',
      sub: [
        { name: 'Delivery/Pickup', state: 'delivery_pickup', value: '', color1: '#1e98d5' },
      ],
    },
  ];

  telehealthPharmacyMenu: IMenuItem[] = [
    {
      name: 'Create New Order',
      type: 'link',
      key: 'create-order',
      icon: 'description',
      state: 'orders/create-order',
    },
    {
      name: 'Telehealth',
      key: 'telehealth',
      type: 'dropDown',
      tooltip: 'Dashboard',
      //TODO: change icon
      svgIcon: 'ulb_telehealth',
      state: 'telehealth',
      value: '',
      sub: [
        {
          name: 'Telehealth Request',
          type: 'link',
          state: 'telehealth-request',
        },
        {
          name: 'Pending',
          type: 'link',
          state: 'pending',
          value: '',
          color1: '#ff7901',
        },
        {
          name: 'Script Issued',
          type: 'link',
          state: 'script-issued',
          value: '',
          color1: '#1e98d5',
        },
        {
          name: 'Awaiting Delivery',
          type: 'link',
          state: 'awaiting-delivery',
          value: '',
          color1: '#1e98d5',
        },
        { name: 'Completed', state: 'completed', value: '', color1: 'grey' },
        { name: 'Cancelled', state: 'cancelled', value: '', color1: 'grey' },
        { name: 'Pending cancellation', state: 'pending_cancellation', value: '', color1: '#1e98d5' }
      ],
    },
    {
      name: 'Resources',
      key: 'resources',
      type: 'dropDown',
      tooltip: 'Dashboard',
      //TODO: change icon
      svgIcon: 'ulb_resources',
      state: 'resources',
      value: '',
      sub: [
        {
          name: 'Collateral',
          type: 'link',
          icon: 'ulb_server',
          state: 'collateral'
        },
        {
          name: 'Telehealth User Guide',
          type: 'link',
          icon: 'ulb_server',
          state: 'telehealth-user-guide',
        },
      ],
    },
    {
      name: 'Stores',
      key: 'stores',
      type: 'dropDown',
      tooltip: 'Dashboard',
      svgIcon: 'ulb_stores',
      state: 'stores',
      sub: [{ name: 'Store Details', state: 'store-detail' }],
    },
  ];

  clinicalOperationsMenu: IMenuItem[] = [
    ...this.telehealthPharmacyMenu,
    {
      name: 'Clinician',
      key: 'clinician',
      type: 'dropDown',
      tooltip: 'Dashboard',
      svgIcon: 'ulb_server',
      state: 'clinician',
      sub: [
        { name: 'Clinician', state: 'profiles' },
        { name: 'Shifts', state: 'shifts' },
        { name: 'Day Sheet', state: 'day-sheet' },
      ],
    },
    {
      name: 'Admin',
      key: 'admin',
      type: 'dropDown',
      tooltip: 'Dashboard',
      svgIcon: 'ulb_admin',
      state: 'admin',
      sub: [{ name: 'Calendars', state: 'calendars' }],
    },
  ];

  radiologyUserMenu: IMenuItem[] = [
    {
      name: 'Facility Management',
      key: 'facility-management',
      type: 'link',
      tooltip: 'Facility Management',
      svgIcon: 'ulb_admin',
      state: 'radiology/facility-management',
    },
    {
      name: 'Performer Maintenance',
      key: 'performer-maintenance',
      type: 'link',
      tooltip: 'Performer Maintenance',
      svgIcon: 'ulb_admin',
      state: 'radiology/performer-maintenance',
    }
  ];

  superAdminMenu: IMenuItem[] = [
    {
      name: 'Create New Order',
      key: 'create-order',
      type: 'link',
      icon: 'description',
      state: 'orders/create-order',
    },
    {
      name: 'Orders',
      key: 'online-orders',
      type: 'dropDown',
      tooltip: 'Dashboard',
      svgIcon: 'ulb_orders',
      state: 'orders/online',
      value: '',
      color1: 'warn',
      sub: [
        // { name: 'Clinic to Review', state: 'clinic_to_review', value: '', color1: "#72cb38" },
        { name: 'Waiting Room', state: 'clinic_to_action', value: '', color1: '#72cb38' },
        { name: 'Pending Approval', state: 'pending_approval', value: '', color1: '#ff7901' },
        { name: 'Price Request', state: 'price_request', value: '', color1: '#1e98d5' },
        { name: 'Ready to Process', state: 'processing', value: '', color1: '#1e98d5' },
        { name: 'Delivery/Pickup', state: 'delivery_pickup', value: '', color1: '#1e98d5' },
        { name: 'Pending cancellation', state: 'pending_cancellation', value: '', color1: '#1e98d5' },
      ],
    },
    {
      name: 'Telehealth',
      key: 'telehealth',
      type: 'dropDown',
      tooltip: 'Dashboard',
      //TODO: change icon
      svgIcon: 'ulb_telehealth',
      state: 'telehealth',
      value: '',
      sub: [
        {
          name: 'Telehealth Request',
          type: 'link',
          state: 'telehealth-request',
        },
        {
          name: 'Consultation Request',
          type: 'link',
          state: 'new-telehealth-request'
        },
        {
          name: 'Pending',
          type: 'link',
          state: 'pending',
          value: '',
          color1: '#ff7901',
        },
        {
          name: 'Script Issued',
          type: 'link',
          state: 'script-issued',
          value: '',
          color1: '#1e98d5',
        },
      ],
    },
    {
      name: 'All Orders',
      key: 'all-orders',
      type: 'dropDown',
      tooltip: 'Dashboard',
      svgIcon: 'ulb_orders',
      state: 'orders/admin',
      value: '',
      color1: 'warn',
      sub: [
        { name: 'Clinic to Review', state: 'clinic_to_review', value: '', color1: '#72cb38' },
        { name: 'Clinic to Update', state: 'clinic_to_update', value: '', color1: '#1e98d5' },
        { name: 'Appointments', state: 'waiting_room', value: '', color1: '#1e98d5' },
        { name: 'Auto Allocation', state: 'auto_allocation', value: '', color1: '#1e98d5' },
        { name: 'Waiting Room', state: 'clinic_to_action', value: '', color1: '#72cb38' },
        { name: 'Urgent', state: 'escalated_orders', value: '', color1: '#F0A04B' },
        {
          name: 'Waiting for Info',
          state: 'waiting_for_information',
          value: '',
          color1: '#72cb38',
        },
        { name: 'Request Issued', state: 'request_issued', value: '', color1: '#1e98d5' },
        { name: 'Pending Approval', state: 'pending_approval', value: '', color1: '#ff7901' },
        { name: 'Price Request', state: 'price_request', value: '', color1: '#1e98d5' },
        { name: 'Ready to Process', state: 'processing', value: '', color1: '#1e98d5' },
        { name: 'Delivery/Pickup', state: 'delivery_pickup', value: '', color1: '#1e98d5' },
        { name: 'Active', state: 'active', value: '', color1: '#ff3d57' },
        { name: 'Exception', state: 'exception', value: '', color1: 'pink' },
        { name: 'Pending Batch Processing', state: 'pending_batch_processing', value: '', color1: '#ff7901' },
        { name: 'Paper Scripts', state: 'paper_scripts', value: '', color1: '#ff3d57' },
        { name: 'Care Call', state: 'care_call', value: '', color1: 'pink' }
      ],
    },
    {
      name: 'Closed',
      key: 'closed-orders',
      type: 'dropDown',
      tooltip: 'Dashboard',
      svgIcon: 'ulb_closed',
      state: 'orders/closed',
      value: '',
      color1: 'warn',
      sub: [
        { name: 'Completed', state: 'completed', value: '', color1: 'grey' },
        { name: 'Cancelled', state: 'cancelled', value: '', color1: 'grey' },
      ],
    },
    {
      name: 'Programs',
      key: 'patient-programs',
      type: 'link',
      tooltip: 'Programs',
      svgIcon: 'ulb_checklist',
      state: 'patient-programs',
    },
    {
      name: 'Users',
      key: 'users',
      type: 'dropDown',
      tooltip: 'Dashboard',
      svgIcon: 'ulb_users',
      state: 'users',
      value: '',
      sub: [
        {
          name: 'Invite',
          type: 'link',
          icon: 'person_add',
          state: 'invite',
        },
        {
          name: 'Create User',
          type: 'link',
          icon: 'person_add',
          state: 'create-user',
        },
        {
          name: 'Manage User',
          type: 'link',
          icon: 'person_add',
          state: 'manage-user',
        },
      ],
    },
    {
      name: 'Resources',
      key: 'resources',
      type: 'dropDown',
      tooltip: 'Dashboard',
      //TODO: change icon
      svgIcon: 'ulb_resources',
      state: 'resources',
      value: '',
      sub: [
        {
          name: 'Create New Resource',
          type: 'link',
          icon: 'add_circle',
          state: 'create-resource',
        },
        {
          name: 'On Boarding',
          type: 'link',
          icon: 'ulb_server',
          state: 'on-boarding',
        },
        {
          name: 'Setup',
          type: 'link',
          icon: 'ulb_server',
          state: 'setup',
        },
        {
          name: 'Training',
          type: 'link',
          icon: 'ulb_server',
          state: 'training-guide',
        },
        {
          name: 'Program',
          type: 'link',
          icon: 'person_add',
          state: 'pharmacy-program'
        },
        {
          name: 'Treatment guides',
          type: 'link',
          icon: 'ulb_server',
          state: 'treatment-guides',
        },
        {
          name: 'Learning and Development',
          type: 'link',
          icon: 'ulb_server',
          state: 'learning-and-development',
        },
        {
          name: 'Telehealth User Guide',
          type: 'link',
          icon: 'ulb_server',
          state: 'telehealth-user-guide'
        },
        {
          name: 'Feature Tours',
          type: 'link',
          icon: 'tour',
          state: 'feature-tours',
        },
      ]
    },
    {
      name: 'Reports',
      key: 'reports',
      type: 'dropDown',
      icon: 'description',
      state: 'reports',
      sub: [
        { name: 'Dashboard', state: 'dashboard' },
        { name: 'Location', state: 'locations' },
        { name: 'Partners', state: 'partners' },
        { name: 'Telehealth', state: 'telehealth' },
        { name: 'Programs', state: 'programs' },
      ],
    },
    {
      name: 'Stores',
      key: 'stores',
      type: 'dropDown',
      tooltip: 'Dashboard',
      svgIcon: 'ulb_stores',
      state: 'stores',
      sub: [
        { name: 'New Store', state: 'create-store' },
        { name: 'Store Details', state: 'store-detail' },
        { name: 'Store Listing', state: 'listing' },
        // { name: 'Delivery Options', state: 'analytics' },
        // { name: 'Delivery Partners', state: 'deliverypartners' },
      ],
    },
    {
      name: 'Clinician',
      key: 'clinician',
      type: 'dropDown',
      tooltip: 'Dashboard',
      svgIcon: 'ulb_server',
      state: 'clinician',
      sub: [
        { name: 'Clinician', state: 'profiles' },
        { name: 'Shifts', state: 'shifts' },
        { name: 'Day Sheet', state: 'day-sheet' },
      ],
    },
    {
      name: 'Products',
      key: 'products',
      type: 'dropDown',
      tooltip: 'Dashboard',
      svgIcon: 'ulb_products',
      state: 'products',
      sub: [
        { name: 'Master Catalogue', state: 'master-catalogue' },
        { name: 'Catalogue', state: 'catalogue' },
        { name: 'Promotions', state: 'promotions' },
        { name: 'Categories', state: 'categories' },
        { name: 'Brands', state: 'brands' },
      ],
    },
    {
      name: 'Rendr',
      key: 'rendr',
      type: 'link',
      tooltip: 'Dashboard',
      svgIcon: 'ulb_rendr',
      state: 'orders/rendr',
    },
    {
      name: 'Billing',
      key: 'billing',
      type: 'dropDown',
      tooltip: 'Billing',
      svgIcon: 'ulb_billing',
      state: 'billing',
      sub: [
        { name: 'Invoice Management', state: 'manage' },
        // Legacy Stripe Subscriptions and Patient Programs coexist, so the
        // Subscriptions screen stays available for patients still on the
        // legacy billing model.
        { name: 'Subscriptions', state: 'subscriptions' },
      ],
    },
    {
      name: 'Admin',
      key: 'admin',
      type: 'dropDown',
      tooltip: 'Dashboard',
      svgIcon: 'ulb_admin',
      state: 'admin',
      sub: [
        { name: 'Known As', state: 'default' },
        { name: 'Notification Monitor', state: 'monitor' },
        { name: 'Restricted Meds', state: 'restricted-meds' },
        { name: 'Manage Banner', state: 'manage-banner' },
        { name: 'Telehealth Config', state: 'telehealth-configurations' },
        { name: 'Approval Requests', state: 'approval-requests' },
        { name: 'Facility Management', state: 'facility-management' },
        { name: 'Performer Maint...', state: 'performer-maintenance' },
        { name: 'Telehealth', state: 'telehealth' },
        { name: 'Calendars', state: 'calendars' },
        { name: 'API Service Hours', state: 'api-service-hours' },
      ],
    },
  ];

  newOrderCount: OrderCount;

  // Icon menu TITLE at the very top of navigation.
  // This title will appear if any icon type item is present in menu.
  iconTypeMenuTitle = 'Frequently Accessed';
  // sets portalUserMenu as default;
  menuItems = new BehaviorSubject<IMenuItem[]>(this.portalUserMenu);
  // navigation component has subscribed to this Observable
  menuItems$ = this.menuItems.asObservable();

  constructor(
    private http: HttpClient,
    private jwtAuth: JwtAuthService,
    private routeProtectionService: RouteProtectionService
  ) { }

  // Customizer component uses this method to change menu.
  // You can remove this method and customizer component.
  // Or you can customize this method to supply different menu for
  // different user type.
  publishNavigationChange(menuType: MenuType) {
    try {
      let menu: IMenuItem[] = this[toCamelCase(menuType)];

      //configure menu by iterating through hidden keys object. Exclude any menu items if key exists
      menu = this.routeProtectionService.filterMenu(menu);

      this.menuItems.next(menu);
    } catch (error) {
      throw new Error('failed to load menu: ' + error);
    }
  }
  publishNavigation(role: UserRole) {
    switch (role) {
      case ProfileType.Admin:
        this.publishNavigationChange('admin-menu');
        break;
      case ProfileType.SuperAdmin:
        this.publishNavigationChange('super-admin-menu');
        break;
      case ProfileType.PortalUser:
      case ProfileType.StandardPharmacy:
        this.publishNavigationChange('portal-user-menu');
        break;
      case ProfileType.TelehealthPharmacy:
        this.publishNavigationChange('telehealth-pharmacy-menu');
        break;
      case ProfileType.ClinicalOperations:
        this.publishNavigationChange('clinical-operations-menu');
        break;
      case ProfileType.SuperBusinessUser:
      case ProfileType.BusinessUser:
        this.publishNavigationChange('business-user-menu');
        break;
      case ProfileType.Driver:
        this.publishNavigationChange('driver-menu');
        break;
      case ProfileType.Doctor:
        this.publishNavigationChange('doctor-user-menu');
        break;
      case ProfileType.NursePractitioner:
        this.publishNavigationChange('nurse-practitioner-menu');
        break;
      case ProfileType.Nurse:
        this.publishNavigationChange('nurse-menu');
        break;
      case ProfileType.RadiologyUser:
        this.publishNavigationChange('radiology-user-menu');
        break;
      default:
        break;
    }
  }

  set_counts(menu_item: IMenuItem, counts: OrderCountTypes) {
    menu_item?.sub?.forEach(element => {
      if (element.state === 'paper_scripts') {
        element.value = counts['unsent_paper_scripts'];
      } else {
        element.value = counts[element.state.replace('-', '_')];
      }
    });
  }

  setOrderCount(ordersCount: OrderCount) {
    this.newOrderCount = ordersCount;

    const menu: IMenuItem[] = this.menuItems.value;

    const online_menu = menu.find(m => m.name === 'Orders');
    const closed_menu = menu.find(m => m.name === 'Closed');
    const telehealth_menu = menu.find(m => m.name === 'Telehealth');
    const all_orders_menu = menu.find(m => m.name === 'All Orders');

    if (online_menu !== null) {
      this.set_counts(online_menu, this.newOrderCount.online);
    }
    if (all_orders_menu !== null) {
      this.set_counts(all_orders_menu, this.newOrderCount.all_orders);
    }
    if (closed_menu !== null) {
      this.set_counts(closed_menu, this.newOrderCount.closed);
    }
    if (telehealth_menu !== null) {
      this.set_counts(telehealth_menu, this.newOrderCount.telehealth);
    }
    this.menuItems.next([...menu]);
  }

  getOrderCountForMenu() {
    const role = this.jwtAuth.getUser().role;
    let searchParams = new HttpParams();

    switch (role) {
      case ProfileType.Admin:
      case ProfileType.SuperAdmin:
        searchParams = searchParams.append('type', 'online,all_orders,closed,telehealth');
        break;
      case ProfileType.PortalUser:
      case ProfileType.StandardPharmacy:
      case ProfileType.SuperBusinessUser:
      case ProfileType.BusinessUser:
        searchParams = searchParams.append('type', 'online,closed,telehealth');
        break;
      case ProfileType.TelehealthPharmacy:
      case ProfileType.ClinicalOperations:
        searchParams = searchParams.append('type', 'telehealth');
        break;
      case ProfileType.Doctor:
      case ProfileType.Nurse:
      case ProfileType.NursePractitioner:
        searchParams = searchParams.append('type', 'all_orders,closed');
        break;
      case ProfileType.Driver:
        return;
      default:
        return;
    }

    this.http
      .get<OrderCount>(environment.apiURL1 + 'get_order_count_for_menu_v1', {
        params: searchParams,
      })
      .subscribe(res => {
        this.setOrderCount(res);
      });
  }

  switchView(role: ProfileType, user: User) {
    this.publishNavigation(role);
    this.jwtAuth.setCustomRole(role, user);
  }

}
